import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { Plus, Pencil, Trash2, X, Upload, Download, FileSpreadsheet, List, LayoutGrid, ChevronDown, ChevronUp, CalendarDays, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = { paid: "Ödendi", unpaid: "Bekliyor", partial: "Kısmi", overdue: "Gecikmiş" };
const STATUS_COLORS = { paid: "bg-[#16A34A]", unpaid: "bg-[#F59E0B]", partial: "bg-[#0020C2]", overdue: "bg-[#DC2626]" };

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

const fullMonthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const invMonthOf = (inv) => ((inv.month || "").slice(0, 7)) || ((inv.date || "").slice(0, 7));

const emptyInvoice = () => ({
  invoice_no: "", supplier_id: "", supplier_or_type: "", category: "",
  date: new Date().toISOString().slice(0,10), due_date: "", amount: 0,
  status: "unpaid", month: "", notes: "",
});

export default function InvoicesPage() {
  const [view, setView] = useState("supplier"); // supplier | flat
  const [invoices, setInvoices] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [monthFilter, setMonthFilter] = useState("all");

  // Sorting state
  const [flatSort, setFlatSort] = useState({ key: "date", dir: "desc" });      // liste görünümü
  const [supplierSort, setSupplierSort] = useState("total_desc");              // cari görünümü
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInvoice());
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSupplier, setBulkSupplier] = useState({ id: "", name: "" });
  const [bulkMonths, setBulkMonths] = useState([{ month: new Date().toISOString().slice(0,7), amount: 0, status: "unpaid" }]);

  // Monthly Supplier Excel upload state
  const [showMonthlyUpload, setShowMonthlyUpload] = useState(false);
  const [monthlyFile, setMonthlyFile] = useState(null);
  const [monthlyMonth, setMonthlyMonth] = useState(new Date().toISOString().slice(0,7));
  const [monthlyPreview, setMonthlyPreview] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const load = async () => {
    try {
      const [inv, grp, sup] = await Promise.all([
        api.get("/invoices"),
        api.get("/invoices/by-supplier"),
        api.get("/suppliers"),
      ]);
      setInvoices(inv.data);
      setGrouped(grp.data);
      setSuppliers(sup.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyInvoice()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      invoice_no: r.invoice_no, supplier_id: r.supplier_id || "", supplier_or_type: r.supplier_or_type,
      category: r.category || "", date: r.date, due_date: r.due_date || "",
      amount: r.amount, status: r.status || "unpaid", month: r.month || "", notes: r.notes || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0 };
    if (payload.supplier_id) {
      const s = suppliers.find(x => x.id === payload.supplier_id);
      if (s) payload.supplier_or_type = s.name;
    }
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Kaydedildi");
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Faturayı silmek istiyor musunuz?")) return;
    try { await api.delete(`/invoices/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openBulk = (sup) => {
    setBulkSupplier({ id: sup?.id || sup?.supplier_id || "", name: sup?.name || sup?.supplier_name || "" });
    setBulkMonths([{ month: new Date().toISOString().slice(0,7), amount: 0, status: "unpaid" }]);
    setShowBulk(true);
  };

  const submitBulk = async () => {
    if (!bulkSupplier.name) { toast.error("Tedarikçi seçin"); return; }
    const valid = bulkMonths.filter(m => m.month && Number(m.amount) > 0);
    if (valid.length === 0) { toast.error("En az bir geçerli ay girin"); return; }
    try {
      const { data } = await api.post("/invoices/supplier-monthly", {
        supplier_id: bulkSupplier.id,
        supplier_name: bulkSupplier.name,
        months: valid,
      });
      toast.success(`${data.inserted} aylık fatura eklendi`);
      setShowBulk(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} fatura yüklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openMonthlyUpload = () => {
    setMonthlyFile(null);
    setMonthlyPreview(null);
    setMonthlyMonth(new Date().toISOString().slice(0,7));
    setShowMonthlyUpload(true);
  };

  const runMonthlyPreview = async () => {
    if (!monthlyFile) { toast.error("Dosya seçin"); return; }
    if (!monthlyMonth) { toast.error("Ay seçin"); return; }
    const fd = new FormData();
    fd.append("file", monthlyFile);
    fd.append("month", monthlyMonth);
    fd.append("preview", "true");
    setMonthlyLoading(true);
    try {
      const { data } = await api.post("/invoices/upload-monthly-supplier", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMonthlyPreview(data);
      toast.success(`${data.totals?.rows || 0} satır okundu`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setMonthlyLoading(false); }
  };

  const runMonthlyCommit = async () => {
    if (!monthlyFile || !monthlyMonth) return;
    const fd = new FormData();
    fd.append("file", monthlyFile);
    fd.append("month", monthlyMonth);
    fd.append("preview", "false");
    setMonthlyLoading(true);
    try {
      const { data } = await api.post("/invoices/upload-monthly-supplier", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created_invoices} fatura, ${data.created_payments} ödeme, ${data.created_suppliers} yeni cari eklendi${data.skipped_existing ? ` (${data.skipped_existing} zaten vardı)` : ""}`);
      setShowMonthlyUpload(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setMonthlyLoading(false); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/invoices/template/xlsx?auth=${token || ""}`, "_blank");
  };

  const toggle = (id) => {
    const s = new Set(expanded);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpanded(s);
  };

  const grandTotal = grouped.reduce((a, g) => a + g.total_amount, 0);
  const grandUnpaid = grouped.reduce((a, g) => a + g.unpaid_amount, 0);

  // Ay filtresi seçenekleri (verideki aylardan)
  const monthOptions = [...new Set(invoices.map(invMonthOf).filter(Boolean))].sort().reverse();

  // Ay filtresi uygulanmış veri setleri
  const filteredInvoices = monthFilter === "all" ? invoices : invoices.filter((i) => invMonthOf(i) === monthFilter);
  const filteredGrouped = monthFilter === "all" ? grouped : grouped
    .map((g) => {
      const monthly = (g.monthly || []).filter((mm) => mm.month === monthFilter);
      const invs = monthly.flatMap((mm) => mm.invoices || []);
      const total = invs.reduce((a, i) => a + (Number(i.amount) || 0), 0);
      const paid = invs.filter((i) => i.status === "paid").reduce((a, i) => a + (Number(i.amount) || 0), 0);
      return { ...g, monthly, invoices: invs, invoice_count: invs.length, total_amount: total, paid_amount: paid, unpaid_amount: total - paid };
    })
    .filter((g) => g.invoice_count > 0);
  const shownTotal = monthFilter === "all" ? grandTotal : filteredGrouped.reduce((a, g) => a + g.total_amount, 0);
  const shownUnpaid = monthFilter === "all" ? grandUnpaid : filteredGrouped.reduce((a, g) => a + g.unpaid_amount, 0);

  // Sorting for supplier view (grouped)
  const sortedGrouped = (() => {
    const arr = [...filteredGrouped];
    switch (supplierSort) {
      case "name_asc":     return arr.sort((a, b) => (a.supplier_name || "").localeCompare(b.supplier_name || "", "tr-TR"));
      case "name_desc":    return arr.sort((a, b) => (b.supplier_name || "").localeCompare(a.supplier_name || "", "tr-TR"));
      case "total_asc":    return arr.sort((a, b) => a.total_amount - b.total_amount);
      case "total_desc":   return arr.sort((a, b) => b.total_amount - a.total_amount);
      case "unpaid_asc":   return arr.sort((a, b) => a.unpaid_amount - b.unpaid_amount);
      case "unpaid_desc":  return arr.sort((a, b) => b.unpaid_amount - a.unpaid_amount);
      case "count_asc":    return arr.sort((a, b) => a.invoice_count - b.invoice_count);
      case "count_desc":   return arr.sort((a, b) => b.invoice_count - a.invoice_count);
      default:             return arr;
    }
  })();

  // Sorting for flat view
  const sortedInvoices = (() => {
    const { key, dir } = flatSort;
    const mult = dir === "asc" ? 1 : -1;
    return [...filteredInvoices].sort((a, b) => {
      let va = a[key], vb = b[key];
      if (key === "amount") { va = Number(va) || 0; vb = Number(vb) || 0; return (va - vb) * mult; }
      if (key === "date" || key === "due_date") { va = va || ""; vb = vb || ""; return va.localeCompare(vb) * mult; }
      va = (va || "").toString(); vb = (vb || "").toString();
      return va.localeCompare(vb, "tr-TR") * mult;
    });
  })();

  const toggleFlatSort = (key) => {
    setFlatSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "amount" || key === "date" || key === "due_date" ? "desc" : "asc" });
  };

  const SortIcon = ({ column }) => {
    if (flatSort.key !== column) return <ArrowUpDown className="w-3 h-3 inline-block text-neutral-400" />;
    return flatSort.dir === "asc"
      ? <ArrowUp className="w-3 h-3 inline-block text-[#0020C2]" />
      : <ArrowDown className="w-3 h-3 inline-block text-[#0020C2]" />;
  };

  return (
    <>
      <PageHeader
        title="Tedarikçi Faturaları"
        subtitle="Cari (tedarikçi) bazlı fatura yönetimi. Her tedarikçi için aylık faturaları tek seferde ekleyin veya Excel ile yükleyin."
        testid="invoices-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {invoices.length > 0 && (
              <ExportButtons
                filename="tedarikci_faturalari"
                title="Tedarikçi Faturaları"
                subtitle={`Toplam: ${formatTL(grandTotal)} · Bekleyen: ${formatTL(grandUnpaid)}`}
                sections={[
                  {
                    name: "Faturalar", heading: "Fatura Listesi",
                    columns: [
                      { key: "invoice_no", label: "No" },
                      { key: "supplier_or_type", label: "Tedarikçi" },
                      { key: "category", label: "Kategori" },
                      { key: "date", label: "Tarih", get: (r) => formatDate(r.date) },
                      { key: "due_date", label: "Vade", get: (r) => formatDate(r.due_date) },
                      { key: "amount", label: "Tutar", format: "money" },
                      { key: "status", label: "Durum", get: (r) => STATUS_LABELS[r.status] || r.status },
                    ],
                    rows: invoices,
                  },
                  {
                    name: "Tedarikçi Özeti", heading: "Tedarikçi Bazlı Özet",
                    columns: [
                      { key: "supplier_name", label: "Tedarikçi" },
                      { key: "invoice_count", label: "Fatura Sayısı", format: "number" },
                      { key: "total_amount", label: "Toplam", format: "money" },
                      { key: "paid_amount", label: "Ödendi", format: "money" },
                      { key: "unpaid_amount", label: "Bekliyor", format: "money" },
                    ],
                    rows: grouped.filter(g => g.invoice_count > 0),
                  },
                ]}
              />
            )}
            <button data-testid="inv-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Download className="w-3.5 h-3.5" /> Boş Excel
            </button>
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <FileSpreadsheet className="w-3.5 h-3.5" /> <span data-testid="inv-xlsx-upload">Excel Yükle</span>
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="inv-monthly-upload-btn" onClick={openMonthlyUpload} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors">
              <CalendarDays className="w-3.5 h-3.5" /> Aylık Ted. Excel
            </button>
            <button data-testid="inv-bulk-btn" onClick={() => openBulk(null)} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Upload className="w-3.5 h-3.5" /> Aylık Toplu Ekle
            </button>
            <button data-testid="inv-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Fatura
            </button>
          </div>
        }
      />

      {/* View toggle */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-white border border-[#E5E5E5] p-1">
          <button data-testid="view-supplier-btn" onClick={() => setView("supplier")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${view === "supplier" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F5F5F5]"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Cari Bazlı
          </button>
          <button data-testid="view-flat-btn" onClick={() => setView("flat")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${view === "flat" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F5F5F5]"}`}>
            <List className="w-3.5 h-3.5" /> Liste
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ay</span>
            <select data-testid="inv-month-filter" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
              className={`px-2 py-1.5 text-xs border bg-white ${monthFilter !== "all" ? "border-[#0020C2] text-[#0020C2] font-semibold" : "border-[#E5E5E5]"}`}>
              <option value="all">Tüm Aylar</option>
              {monthOptions.map(m => <option key={m} value={m}>{fullMonthLabel(m)}</option>)}
            </select>
            {monthFilter !== "all" && (
              <button data-testid="inv-month-clear" onClick={() => setMonthFilter("all")} className="p-1 hover:bg-[#F5F5F5]" title="Ay filtresini temizle">
                <X className="w-3.5 h-3.5 text-neutral-500" />
              </button>
            )}
          </div>
          {view === "supplier" && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Sırala</span>
              <select data-testid="supplier-sort-select" value={supplierSort} onChange={e => setSupplierSort(e.target.value)}
                className="px-2 py-1.5 text-xs border border-[#E5E5E5] bg-white">
                <option value="total_desc">Toplam ↓ (Yüksek → Düşük)</option>
                <option value="total_asc">Toplam ↑ (Düşük → Yüksek)</option>
                <option value="unpaid_desc">Bekleyen ↓</option>
                <option value="unpaid_asc">Bekleyen ↑</option>
                <option value="count_desc">Fatura Sayısı ↓</option>
                <option value="count_asc">Fatura Sayısı ↑</option>
                <option value="name_asc">Tedarikçi (A→Z)</option>
                <option value="name_desc">Tedarikçi (Z→A)</option>
              </select>
            </div>
          )}
          <div className="text-xs text-neutral-500">
            {monthFilter !== "all" && <span className="font-semibold text-[#0020C2]">{fullMonthLabel(monthFilter)}: </span>}
            <span className="num font-semibold">{formatTL(shownTotal)}</span> toplam · <span className="num font-semibold text-[#F59E0B]">{formatTL(shownUnpaid)}</span> bekliyor
          </div>
        </div>
      </div>

      {view === "supplier" && (
        <div className="space-y-3">
          {sortedGrouped.filter(g => g.invoice_count > 0 || g.supplier_id).length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
              Henüz tedarikçi/fatura kaydı yok.
            </div>
          ) : sortedGrouped.map(g => {
            const isOpen = expanded.has(g.supplier_id || g.supplier_name);
            return (
              <div key={g.supplier_id || g.supplier_name} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`supplier-group-${g.supplier_name}`}>
                <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                  <button onClick={() => toggle(g.supplier_id || g.supplier_name)} className="flex items-center gap-3 text-left flex-1 min-w-0">
                    {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-lg truncate">{g.supplier_name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        <span className="num font-semibold">{g.invoice_count}</span> fatura
                        {g.monthly.length > 0 && <> · Son ay: {monthLabel(g.monthly[0].month)}</>}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam</div>
                      <div className="num font-bold text-base">{formatTL(g.total_amount)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödendi</div>
                      <div className="num font-bold text-base text-[#16A34A]">{formatTL(g.paid_amount)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bekliyor</div>
                      <div className="num font-bold text-base text-[#F59E0B]">{formatTL(g.unpaid_amount)}</div>
                    </div>
                    <button
                      data-testid={`add-monthly-${g.supplier_name}`}
                      onClick={() => openBulk(g)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]"
                    >
                      <Plus className="w-3 h-3" /> Aylık Ekle
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#F5F5F5] p-4 space-y-3">
                    {g.monthly.length === 0 ? (
                      <div className="text-xs text-neutral-500 text-center py-6">Bu tedarikçi için henüz fatura yok. &quot;Aylık Ekle&quot; ile başlayın.</div>
                    ) : g.monthly.map(m => (
                      <div key={m.month} className="border border-[#F5F5F5] rounded overflow-hidden">
                        <div className="px-3 py-2 bg-[#FAFAFA] flex items-center justify-between text-xs">
                          <div className="font-semibold">{monthLabel(m.month)}</div>
                          <div className="flex items-center gap-4">
                            <span className="text-neutral-500 num">{m.count} kalem</span>
                            <span className="num font-semibold">{formatTL(m.amount)}</span>
                          </div>
                        </div>
                        <table className="w-full text-xs">
                          <tbody>
                            {m.invoices.map(inv => (
                              <tr key={inv.id} className="border-t border-[#F5F5F5]">
                                <td className="px-3 py-2 num text-neutral-600 w-24">{inv.invoice_no}</td>
                                <td className="px-3 py-2 text-neutral-600">{formatDate(inv.date)}</td>
                                <td className="px-3 py-2 text-neutral-500">{inv.category || "-"}</td>
                                <td className="px-3 py-2 text-right num font-semibold">{formatTL(inv.amount)}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${STATUS_COLORS[inv.status] || "bg-neutral-400"}`}>
                                    {STATUS_LABELS[inv.status] || inv.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="inline-flex gap-1">
                                    <button onClick={() => openEdit(inv)} className="p-1 hover:bg-[#F5F5F5]"><Pencil className="w-3 h-3" /></button>
                                    <button onClick={() => remove(inv)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "flat" && (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="p-16 text-center text-sm text-neutral-500">{monthFilter !== "all" ? `${fullMonthLabel(monthFilter)} ayında fatura kaydı yok.` : "Fatura kaydı yok."}</div>
          ) : (
            <div className="overflow-x-auto data-table">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    {[
                      { key: "invoice_no", label: "No" },
                      { key: "supplier_or_type", label: "Tedarikçi" },
                      { key: "category", label: "Kategori" },
                      { key: "date", label: "Tarih" },
                      { key: "due_date", label: "Vade" },
                      { key: "amount", label: "Tutar", right: true },
                      { key: "status", label: "Durum" },
                    ].map(h => (
                      <th key={h.key} data-testid={`sort-${h.key}`}
                        onClick={() => toggleFlatSort(h.key)}
                        className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold cursor-pointer select-none hover:text-[#0A0A0A] ${h.right ? "text-right" : "text-left"}`}>
                        <span className="inline-flex items-center gap-1.5">
                          {h.label} <SortIcon column={h.key} />
                        </span>
                      </th>
                    ))}
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map(r => (
                    <tr key={r.id} className="border-b border-[#F5F5F5]">
                      <td className="px-3 py-3 num">{r.invoice_no}</td>
                      <td className="px-3 py-3 font-medium">{r.supplier_or_type}</td>
                      <td className="px-3 py-3 text-neutral-600">{r.category || "-"}</td>
                      <td className="px-3 py-3">{formatDate(r.date)}</td>
                      <td className="px-3 py-3">{formatDate(r.due_date)}</td>
                      <td className="px-3 py-3 text-right num font-semibold">{formatTL(r.amount)}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 text-white ${STATUS_COLORS[r.status] || "bg-neutral-400"}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => openEdit(r)} className="p-1 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(r)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Single invoice form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Tedarikçi Faturası</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura No *</label>
                <input required value={form.invoice_no} onChange={e => setForm({...form, invoice_no: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tedarikçi *</label>
                <select required value={form.supplier_id || form.supplier_or_type} onChange={e => {
                  const v = e.target.value;
                  const s = suppliers.find(x => x.id === v);
                  setForm({...form, supplier_id: s ? s.id : "", supplier_or_type: s ? s.name : v});
                }} className="input mt-1.5">
                  <option value="">Seçiniz</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Durum</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input mt-1.5">
                  <option value="unpaid">Bekliyor</option>
                  <option value="paid">Ödendi</option>
                  <option value="overdue">Gecikmiş</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura Tarihi *</label>
                <input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vade Tarihi</label>
                <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input type="number" step="any" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Dönem (YYYY-MM)</label>
                <input type="month" value={form.month} onChange={e => setForm({...form, month: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk monthly modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-3xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Ekle</div>
                <div className="font-heading font-bold text-lg">Cari Bazlı Aylık Faturalar</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tedarikçi (Cari) *</label>
                <select data-testid="bulk-supplier-select" value={bulkSupplier.id || bulkSupplier.name} onChange={e => {
                  const v = e.target.value;
                  const s = suppliers.find(x => x.id === v);
                  setBulkSupplier(s ? { id: s.id, name: s.name } : { id: "", name: v });
                }} className="input mt-1.5">
                  <option value="">Seçiniz</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {bulkSupplier.name && !bulkSupplier.id && (
                  <div className="text-[11px] text-neutral-500 mt-1">Yeni cari — otomatik eklenecek</div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Aylık Faturalar</label>
                  <button onClick={() => setBulkMonths([...bulkMonths, { month: "", amount: 0, status: "unpaid" }])} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                    <Plus className="w-3 h-3" /> Ay Ekle
                  </button>
                </div>
                <table className="w-full text-xs border border-[#E5E5E5]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>
                      {["Dönem (YYYY-MM)","Fatura No","Kategori","Tutar (₺)","Durum","Not",""].map(h => (
                        <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkMonths.map((m, i) => (
                      <tr key={i} className="border-t border-[#F5F5F5]">
                        <td className="px-2 py-2">
                          <input data-testid={`bulk-month-${i}`} type="month" value={m.month} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], month: e.target.value }; setBulkMonths(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num" />
                        </td>
                        <td className="px-2 py-2">
                          <input placeholder="opsiyonel" value={m.invoice_no || ""} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], invoice_no: e.target.value }; setBulkMonths(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                        </td>
                        <td className="px-2 py-2">
                          <input value={m.category || ""} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], category: e.target.value }; setBulkMonths(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                        </td>
                        <td className="px-2 py-2">
                          <input data-testid={`bulk-amount-${i}`} type="number" step="any" value={m.amount} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], amount: e.target.value }; setBulkMonths(arr); }} className="w-24 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                        </td>
                        <td className="px-2 py-2">
                          <select value={m.status} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], status: e.target.value }; setBulkMonths(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs">
                            <option value="unpaid">Bekliyor</option>
                            <option value="paid">Ödendi</option>
                            <option value="overdue">Gecikmiş</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input value={m.notes || ""} onChange={e => { const arr = [...bulkMonths]; arr[i] = { ...arr[i], notes: e.target.value }; setBulkMonths(arr); }} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                        </td>
                        <td className="px-2 py-2">
                          {bulkMonths.length > 1 && <button onClick={() => setBulkMonths(bulkMonths.filter((_, idx) => idx !== i))} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[11px] text-neutral-500 mt-2">
                  💡 12 ay için hızlı doldurma:
                  <button onClick={() => {
                    const y = new Date().getFullYear();
                    setBulkMonths(Array.from({length: 12}, (_, i) => ({
                      month: `${y}-${String(i+1).padStart(2,'0')}`, amount: 0, status: "unpaid",
                    })));
                  }} className="ml-2 text-[#0020C2] font-semibold hover:underline">{new Date().getFullYear()} Yılının 12 Ayı</button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="bulk-submit-btn" onClick={submitBulk} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Toplu Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Monthly Supplier XLSX Upload Modal */}
      {showMonthlyUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowMonthlyUpload(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Aylık Tedarikçi Excel Yükleme</div>
              </div>
              <button onClick={() => setShowMonthlyUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="monthly-upload-close"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-2">📋 Beklenen Excel formatı:</div>
                <div className="num">Sütunlar: <span className="font-semibold">SIRA NO · SÜRÜCÜ AD SOYAD · KESİLEN FATURA (PERSONEL / OKUL) · TOPLAM TUTAR · AVANS · YAKIT · YAPILACAK ÖDEME · YAPILAN ÖDEME</span></div>
                <div className="mt-2 text-neutral-500">Her satır bir tedarikçinin ay bazlı faturasıdır. Sistem: <span className="font-semibold">1 fatura + Avans/Yakıt/Ödeme için ayrı ayrı tedarikçi ödemesi</span> oluşturur. Yeni tedarikçiler otomatik açılır. Aynı ay için aynı tedarikçinin faturası varsa atlanır (çift kayıt önleme).</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ay (Dönem) *</label>
                  <input type="month" value={monthlyMonth} onChange={e => { setMonthlyMonth(e.target.value); setMonthlyPreview(null); }} className="input mt-1.5 num" data-testid="monthly-upload-month" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                  <input type="file" accept=".xlsx" onChange={e => { setMonthlyFile(e.target.files?.[0] || null); setMonthlyPreview(null); }} className="input mt-1.5" data-testid="monthly-upload-file" />
                </div>
              </div>

              {!monthlyPreview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={runMonthlyPreview} disabled={monthlyLoading || !monthlyFile || !monthlyMonth} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40" data-testid="monthly-upload-preview-btn">
                    {monthlyLoading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {monthlyPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Satır</div>
                      <div className="num font-bold text-lg">{monthlyPreview.totals?.rows || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Fatura</div>
                      <div className="num font-bold text-base">{formatTL(monthlyPreview.totals?.total_invoiced || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Avans</div>
                      <div className="num font-bold text-base text-[#F59E0B]">{formatTL(monthlyPreview.totals?.total_advance || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Yakıt Kes.</div>
                      <div className="num font-bold text-base text-[#DC2626]">{formatTL(monthlyPreview.totals?.total_fuel || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenen</div>
                      <div className="num font-bold text-base text-[#16A34A]">{formatTL(monthlyPreview.totals?.total_paid || 0)}</div>
                    </div>
                  </div>

                  <div className="border border-[#E5E5E5] rounded overflow-hidden max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          {["#","Tedarikçi","Personel","Okul","Toplam","Avans","Yakıt","Yapılacak","Yapılan"].map((h, i) => (
                            <th key={h} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(monthlyPreview.rows || []).map((r, i) => (
                          <tr key={i} className="border-t border-[#F5F5F5]">
                            <td className="px-2 py-1.5 num text-neutral-500">{r.sira ?? i+1}</td>
                            <td className="px-2 py-1.5 font-medium">{r.supplier_name}</td>
                            <td className="px-2 py-1.5 num text-right">{r.personnel_amount > 0 ? formatTL(r.personnel_amount) : "-"}</td>
                            <td className="px-2 py-1.5 num text-right">{r.school_amount > 0 ? formatTL(r.school_amount) : "-"}</td>
                            <td className="px-2 py-1.5 num text-right font-semibold">{formatTL(r.total_amount)}</td>
                            <td className="px-2 py-1.5 num text-right text-[#F59E0B]">{r.advance > 0 ? formatTL(r.advance) : "-"}</td>
                            <td className="px-2 py-1.5 num text-right text-[#DC2626]">{r.fuel_deduction > 0 ? formatTL(r.fuel_deduction) : "-"}</td>
                            <td className="px-2 py-1.5 num text-right">{formatTL(r.payment_due)}</td>
                            <td className="px-2 py-1.5 num text-right text-[#16A34A] font-semibold">{formatTL(r.payment_made)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setMonthlyPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowMonthlyUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button onClick={runMonthlyCommit} disabled={monthlyLoading} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40" data-testid="monthly-upload-commit-btn">
                        {monthlyLoading ? "Kaydediliyor..." : `✓ ${monthlyPreview.totals?.rows || 0} Satırı Sisteme İşle`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
